import { createSlice } from '@reduxjs/toolkit';
// utils
import { axiosInstance, axiosSgcInstance } from '../../utils/axios';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  events: [],
  isOpenModal: false,
  selectedEventId: null,
  selectedRange: null,
};

const slice = createSlice({
  name: 'financeProduct',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // CREATE EVENT
    createEventSuccess(state, action) {
      const newEvent = action.payload;
      state.isLoading = false;
      state.events = [...state.events, newEvent];
    },
  },
});

// Reducer
export default slice.reducer;

export function createProducto(newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.post('api/v1/control/financial-product-clone/', newEvent);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function setPendingChanges(uuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.post(`api/v1/control/financial-product/${uuid}/set_pending_changes/`);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function createProductoMirror(newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.post('api/v1/control/clone-product/', newEvent);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function approveChanges(uuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.post(`api/v1/control/clone-product/${uuid}/approve/`);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function declineChanges(uuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.post(`api/v1/control/clone-product/${uuid}/reject/`);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function getProductByUuid(uuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.get(`api/v1/control/financial-product-clone/${uuid}`);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function getProductList({ includeInactive = false } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.get('api/v1/control/financial-product/', {
        params: {
          include_inactive: includeInactive,
        },
      });

      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function deleteProductByUuid(uuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.delete(`api/v1/control/financial-product/${uuid}/`);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function approveProductByUuid(uuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.post(`api/v1/control/financial-product/${uuid}/approve/`);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function configProduct(newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.post('api/v1/control/configuration-product-clone/', newEvent);
      await dispatch(setPendingChanges(newEvent.product));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function updateConfigProduct(uuid, newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.put(`api/v1/control/configuration-product-clone/${uuid}/`, newEvent);
      await dispatch(setPendingChanges(uuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function getConfigProduct(uuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.get(`api/v1/control/configuration-product-clone/${uuid}`, {
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      });
      if (response.status === 404) {
        return { success: false, error: { response: { status: 404 }, data: response.data } };
      }
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function getConfigProductOriginal(uuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.get(`api/v1/control/configuration-product/${uuid}`);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function createValidation(newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.post('api/v1/control/validation-product-clone/', newEvent);
      await dispatch(setPendingChanges(newEvent.product));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function updateValidation(uuid, newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.put(`api/v1/control/validation-product-clone/${uuid}/`, newEvent);
      await dispatch(setPendingChanges(uuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function deleteValidation(uuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.delete(`api/v1/control/validation-product-clone/${uuid}/`);
      await dispatch(setPendingChanges(uuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function getValidationList(token) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.get(`api/v1/control/validation-product-clone/`, {
        params: { token },
      });
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function createEndFlow(newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.post('api/v1/control/endflow-product-clone/', newEvent);
      await dispatch(setPendingChanges(newEvent.product));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function updateEndFlow(uuid, newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.put(`api/v1/control/endflow-product-clone/${uuid}/`, newEvent);
      await dispatch(setPendingChanges(uuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function deleteEndFlow(uuid, newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.delete(`api/v1/control/endflow-product-clone/${uuid}/`);
      await dispatch(setPendingChanges(uuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function getEndFlowList(token) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.get('api/v1/control/endflow-product-clone/', {
        params: { token },
      });
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function createValidationStep(newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.post('api/v1/control/matrix-clone/', newEvent);
      await dispatch(setPendingChanges(newEvent.product));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function updateValidationStep(uuid, newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.put(`api/v1/control/matrix-clone/${uuid}/`, newEvent);
      await dispatch(setPendingChanges(uuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function deleteValidationStep(uuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.delete(`api/v1/control/matrix-clone/${uuid}/`);
      await dispatch(setPendingChanges(uuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function getValidationStepList(token) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosInstance.get('api/v1/control/matrix-clone/', {
        params: { token },
      });
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function getInteractionStepsList(token) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosSgcInstance.get(`api/v1/interactionstep-clone/?token=${encodeURIComponent(token)}`);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function createInteractionSteps(newEvent, productoUuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosSgcInstance.post('api/v1/interactionstep-clone/', newEvent);
      await dispatch(setPendingChanges(productoUuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function updateInteractionSteps(uuid, newEvent, productoUuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosSgcInstance.put(`api/v1/interactionstep-clone/${uuid}/`, newEvent);
      await dispatch(setPendingChanges(productoUuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function deleteInteractionSteps(uuid, productoUuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosSgcInstance.delete(`api/v1/interactionstep-clone/${uuid}/`);
      await dispatch(setPendingChanges(productoUuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function getInteractionList(token) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosSgcInstance.get(`api/v1/interaction-clone/?token=${encodeURIComponent(token)}`);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function createInteraction(newEvent, productoUuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosSgcInstance.post('api/v1/interaction-clone/', newEvent);
      await dispatch(setPendingChanges(productoUuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function updateInteraction(uuid, newEvent, productoUuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosSgcInstance.put(`api/v1/interaction-clone/${uuid}/`, newEvent);
      await dispatch(setPendingChanges(productoUuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function deleteInteraction(uuid, productoUuid) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosSgcInstance.delete(`api/v1/interaction-clone/${uuid}/`);
      await dispatch(setPendingChanges(productoUuid));
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}

export function createProductoMirrorChatbot(newEvent) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axiosSgcInstance.post('api/v1/clone-product/', newEvent);
      dispatch(slice.actions.createEventSuccess(response.data));
      return { success: true, data: response.data };
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return { success: false, error };
    }
  };
}
